import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import api, { formatError } from "@/api";
import { useAuth } from "@/contexts/AuthContext";
import { Button } from "@/components/ui/button";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { ArrowLeft, Printer, Loader2, BarChart3 } from "lucide-react";
import { toast } from "sonner";
import DateRangeFilter from "@/components/analytics/DateRangeFilter";
import SmartSummaryCard from "@/components/analytics/SmartSummaryCard";
import RevenueTab from "@/components/analytics/RevenueTab";
import PatientsTab from "@/components/analytics/PatientsTab";

const isoDate = (d) => d.toISOString().slice(0, 10);
const today = () => { const d = new Date(); d.setHours(0,0,0,0); return d; };
const daysAgo = (n) => { const d = today(); d.setDate(d.getDate() - n); return d; };

export default function AnalyticsPage() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [range, setRange] = useState({ from: isoDate(daysAgo(6)), to: isoDate(today()), preset: "7d" });
  const [tab, setTab] = useState("revenue");
  const [revenue, setRevenue] = useState(null);
  const [patients, setPatients] = useState(null);
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = async () => {
    setLoading(true);
    try {
      const qs = `from=${range.from}&to=${range.to}`;
      const [r, p, s] = await Promise.all([
        api.get(`/dashboard/admin/analytics/revenue?${qs}`),
        api.get(`/dashboard/admin/analytics/patients?${qs}`),
        api.get(`/dashboard/admin/analytics/summary?${qs}`),
      ]);
      setRevenue(r.data);
      setPatients(p.data);
      setSummary(s.data);
    } catch (err) {
      toast.error(formatError(err.response?.data?.detail));
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); }, [range.from, range.to]);

  const doPrint = () => window.print();

  if (!user) return null;

  return (
    <div className="min-h-screen bg-slate-50" data-testid="analytics-page">
      {/* Print stylesheet */}
      <style>{`
        @media print {
          body { background: white; }
          .no-print, header, .analytics-header-controls { display: none !important; }
          .analytics-tabs [role="tablist"] { display: none !important; }
          .analytics-tabs [role="tabpanel"] { display: block !important; opacity: 1 !important; }
          .medical-card { box-shadow: none !important; border: 1px solid #e2e8f0 !important; break-inside: avoid; }
          @page { margin: 12mm; size: A4; }
        }
      `}</style>

      <header className="bg-white border-b border-slate-200 sticky top-0 z-10 no-print">
        <div className="px-6 py-3 flex items-center justify-between max-w-[1400px] mx-auto gap-3 flex-wrap">
          <div className="flex items-center gap-3">
            <Button variant="ghost" size="icon" onClick={() => navigate("/admin/dashboard")} data-testid="analytics-back-btn">
              <ArrowLeft className="w-4 h-4" />
            </Button>
            <div className="w-9 h-9 rounded-lg bg-gradient-to-br from-sky-500 to-emerald-500 flex items-center justify-center">
              <BarChart3 className="w-5 h-5 text-white" />
            </div>
            <div>
              <h1 className="font-heading text-lg font-semibold text-slate-900">Analytics Dashboard</h1>
              <p className="text-xs text-slate-500">{user.org_name} · {range.from} → {range.to}</p>
            </div>
          </div>
          <div className="flex items-center gap-2 analytics-header-controls flex-wrap">
            <DateRangeFilter value={range} onChange={setRange} />
            <Button variant="outline" size="sm" onClick={doPrint} className="gap-1.5" data-testid="analytics-print-btn">
              <Printer className="w-4 h-4" /> Export PDF
            </Button>
          </div>
        </div>
      </header>

      <main className="px-6 py-8 max-w-[1400px] mx-auto space-y-6">
        {loading && !revenue ? (
          <div className="flex items-center justify-center py-24 text-slate-500 gap-2" data-testid="analytics-loading">
            <Loader2 className="w-5 h-5 animate-spin" /> Loading analytics…
          </div>
        ) : (
          <>
            <SmartSummaryCard data={summary} />
            <Tabs value={tab} onValueChange={setTab} className="analytics-tabs">
              <TabsList data-testid="analytics-tabs">
                <TabsTrigger value="revenue" data-testid="tab-revenue">Revenue &amp; Finance</TabsTrigger>
                <TabsTrigger value="patients" data-testid="tab-patients">Patient Insights</TabsTrigger>
              </TabsList>
              <TabsContent value="revenue" className="mt-6">
                <RevenueTab data={revenue} />
              </TabsContent>
              <TabsContent value="patients" className="mt-6">
                <PatientsTab data={patients} />
              </TabsContent>
            </Tabs>
          </>
        )}
      </main>
    </div>
  );
}
